import {
  Col,
  Row,
  Tag,
  Form,
  List,
  Space,
  Button,
  Select,
  message,
  Skeleton,
  Popconfirm,
  Image,
} from "antd";
import { useContext, useEffect, useState } from "react";
import { DeleteOutlined, FilterOutlined } from "@ant-design/icons";
import EditIcon from "../../ui/EditIcon";

import AddNotes from "./AddNotes";
import ExpandableNoteText from "./ExpandableNoteText";
import useTwitterTimestamp from "../../../hooks/useTwitterTimestamp";
import {
  getPatientMonitoringAPI,
  deletePatientMonitoringAPI,
} from "../../../services/patient.services";
import filterIcon from "../../../assets/images/nurseIcons/filter.svg";
import NoDataPage from "../../NoDataPages/NoDataPage";
import ProgressNotesIcon from "../../../assets/images/nurseIcons/notes.svg";
import LoadingIndicator from "../../Loaders/LoadingIndicator";
import { UserContext } from "../../../context/UserContext";
import useQuery from "../../../hooks/useQuery";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import useIsMobile from "../../../hooks/useIsMobile";
dayjs.extend(utc);
function ProgressMonitoring({ patient }) {
  const [form] = Form.useForm();
  const { userDetails } = useContext(UserContext);
  const isMobile = useIsMobile();

  const userId = userDetails?.id;
  const [data, setData] = useState([]);
  const [selectedRole, setSelectedRole] = useState("all");
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState({
    status: false,
    type: "add",
    id: 0,
  });
  const isActive = useQuery()?.status === "current";
  const [page, setPage] = useState(1);
  const [limit] = useState(100);
  const [total, setTotal] = useState(0);

  async function getData() {
    setLoading(true);

    const params = {
      page,
      limit,
    };

    if (selectedRole !== "all") {
      params.role = selectedRole;
    }

    try {
      const res = await getPatientMonitoringAPI(patient, params);

      const responseData = res.data;

      const list =
        responseData?.results || responseData?.data || responseData || [];

      setData(list);
      setTotal(responseData?.count || list.length || 0);
    } catch (error) {
      console.log(error);
      setData([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (selectedRole) getData();
  }, [selectedRole, page]);
  useEffect(() => {
    setPage(1);
  }, [selectedRole]);

  function updateItem(item) {
    const recordTime = item?.record_time
      ? dayjs.utc(item.record_time).local()
      : dayjs();

    if (isMobile) {
      form.setFieldsValue({
        notes: item?.notes,
        record_date: recordTime,
        record_time_only: recordTime,
        record_time: undefined,
      });
    } else {
      form.setFieldsValue({
        notes: item?.notes,
        record_time: recordTime,
      });
    }

    setIsModalOpen({ status: true, type: "update", id: item?.id });
  }

  function addItem() {
    form.resetFields(["notes", "record_time", "record_date", "record_time_only"]);
    setIsModalOpen({ status: true, type: "add" });
  }

  async function deleteItem(id) {
    try {
      const res = await deletePatientMonitoringAPI(patient, id);
      message.success(res.data.message);
      getData();
    } catch (error) {
      message.success(error.response.data.message);
    }
  }

  const options = [
    { label: "All", value: "all" },
    { label: "Doctor", value: "Doctor" },
    { label: "Nurse", value: "Nurse" },
    { label: "Physiotherapist", value: "Physiotherapist" },
    { label: "Speech Swallow Therapist", value: "Speech_Swallow_Therapist" },
    { label: "Respiratory Therapist", value: "Respiratory_Therapist" },
    { label: "Nutritionist", value: "Nutritionist" },
    { label: "Psychologist", value: "Psychologist" },
  ];

  function RenderItem(item, index) {
    const timestamp = item?.record_time
      ? dayjs.utc(item.record_time).local().format("DD-MM-YYYY h:mm A")
      : dayjs.utc(item?.created_at).local().format("DD-MM-YYYY h:mm A");

    const showButtons = userId === data?.[index]?.done_by_user_id;
    const fullName = `${item?.done_by_first_name || ""} ${
      item?.done_by_last_name || ""
    }`.trim();

    return (
      <List.Item
        className={isMobile ? "progress-monitoring-list-item" : undefined}
        actions={
          showButtons &&
          isActive && [
            <Space size={8}>
              <Button type="secondary" onClick={() => updateItem(item)}>
                <EditIcon style={{ fontSize: "14px" }} />
              </Button>

              <Popconfirm
                title="Delete the task"
                description="Are you sure to delete this record?"
                onConfirm={async () => await deleteItem(item?.id)}
                okText="Yes"
                cancelText="No"
              >
                <Button type="secondary">
                  <DeleteOutlined style={{ color: "red", fontSize: "14px" }} />
                </Button>
              </Popconfirm>
            </Space>,
          ]
        }
      >
        <Skeleton title={false} loading={loading} active>
          <List.Item.Meta
            title={
              isMobile ? (
                <div className="progress-notes-meta-mobile">
                  <span className="progress-notes-name">{fullName}</span>
                  <div className="progress-notes-tags">
                    <Tag color="gold">
                      {item?.done_by_role?.replaceAll?.("_", " ") ||
                        item?.done_by_role}
                    </Tag>
                    <Tag color="geekblue" className="progress-notes-time-tag">
                      {timestamp}
                    </Tag>
                  </div>
                </div>
              ) : (
                <Space>
                  <span>
                    {item?.done_by_first_name} {item?.done_by_last_name}
                  </span>
                  <Tag color="gold">
                    {item?.done_by_role?.replaceAll?.("_", " ") ||
                      item?.done_by_role}
                  </Tag>
                  <Tag color="geekblue" style={{ fontWeight: "normal" }}>
                    {timestamp}
                  </Tag>
                </Space>
              )
            }
            description={
              <ExpandableNoteText
                text={item?.notes}
                rows={2}
                className={isMobile ? "progress-notes-description" : undefined}
                style={{
                  color: "#00000",
                  opacity: 0.6,
                  whiteSpace: "pre-line",
                }}
              />
            }
            // description={item?.notes}
          />
        </Skeleton>
      </List.Item>
    );
  }

  return (
    <Row gutter={[16, 16]} className={isMobile ? "progress-monitoring-page" : undefined}>
      <Col
        span={isMobile ? 24 : 21}
        style={{ display: "flex", justifyContent: isMobile ? "flex-start" : "end" }}
      >
        <Space
          direction={isMobile ? "vertical" : "horizontal"}
          size={isMobile ? 10 : 8}
          className={isMobile ? "progress-monitoring-toolbar" : undefined}
          style={
            isMobile
              ? { width: "100%", alignItems: "stretch" }
              : undefined
          }
        >
          <Space
            size={8}
            align="center"
            className={isMobile ? "progress-monitoring-filter-row" : undefined}
            style={isMobile ? { width: "100%" } : undefined}
          >
            <Image src={filterIcon} preview={false} />
            <Select
              options={options}
              onChange={setSelectedRole}
              defaultValue={selectedRole}
              dropdownClassName={
                isMobile ? "nurse-progress-notes-role-dropdown" : undefined
              }
              style={isMobile ? { width: "100%", minWidth: 0 } : { minWidth: "230px" }}
            />
          </Space>
          <Button
            type="primary"
            onClick={addItem}
            className={isMobile ? "progress-monitoring-add-btn" : undefined}
          >
            Add Notes
          </Button>
        </Space>
        {isModalOpen?.status && (
          <AddNotes
            form={form}
            patient={patient}
            getData={getData}
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            userDetails={userDetails}
          />
        )}
      </Col>
      <Col lg={{ span: 18, offset: 3 }} md={{ span: 24 }} xs={24}>
        {loading ? (
          <div>
            <LoadingIndicator
              style={{ marginBottom: isMobile ? "80px" : "200px" }}
              message="Loading progress data, please wait..."
            />
          </div>
        ) : data?.length > 0 ? (
          <List
            bordered
            className={isMobile ? "progress-monitoring-list" : undefined}
            dataSource={data}
            itemLayout="horizontal"
            renderItem={RenderItem}
            style={{
              backgroundColor: "#fff",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
              maxWidth: "100%",
              overflow: "hidden",
              wordWrap: "break-word",
              whiteSpace: "normal",
            }}
            pagination={
              total > limit
                ? {
                    current: page,
                    pageSize: limit,
                    total,
                    onChange: (p) => setPage(p),
                    showSizeChanger: false,
                    showTotal: (t) => `Total ${t} notes`,
                  }
                : false
            }
            itemStyle={{
              padding: "10px",
              wordWrap: "break-word",
              whiteSpace: "normal",
              overflow: "hidden",
            }}
          />
        ) : (
          <Row
            className={isMobile ? "progress-monitoring-empty" : undefined}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "70vh",
            }}
          >
            <Col span={24} style={{ textAlign: "center" }}>
              <NoDataPage
                title={"Currently, No Progress Notes Have Been Added"}
                subtitle={"Please add new notes or check back later."}
                image={ProgressNotesIcon}
              />
            </Col>
          </Row>
        )}
      </Col>
    </Row>
  );
}

export default ProgressMonitoring;
